import { GDTFUtil } from "@coten-inc/utils";

const DEFAULT_DELIMITER = ".";

export class GDTF {
  readonly gdtf: string;
  readonly date: Date | undefined;
  readonly year: number = 0;
  readonly month?: number;
  readonly day?: number;
  readonly valid: boolean = false;

  constructor(input: string, isStartDate: boolean = true) {
    this.gdtf = input;
    if (!input) {
      // Empty input — silently return invalid
      return;
    }

    const gdtf = (() => {
      // Handle [start..end] format
      if (input.match(/^\[.*\]$/)) {
        const srt = input.replace("[", "").replace("]", "");
        return isStartDate ? srt.split("..")[0] : srt.split("..")[1];
      }
      // Handle start~end format (tilde delimiter)
      if (input.includes("~")) {
        return isStartDate ? input.split("~")[0] : input.split("~")[1];
      }
      return input;
    })();

    const gdtfWithoutY = gdtf.replace(/^Y/, "");
    const isNegativeYear = /^-/.test(gdtfWithoutY);
    if (/^-?\d+x+E\d+$/i.test(gdtfWithoutY)) {
      const [mantissaPart, expStr] = gdtfWithoutY.split(/E/i);
      const filledMantissa = mantissaPart.replace(/x/g, isNegativeYear ? "9" : "0");
      const mantissa = Number(filledMantissa);
      const exp = Number(expStr);
      if (!Number.isNaN(mantissa) && Number.isFinite(exp)) {
        this.year = mantissa * 10 ** exp;
        try {
          const jsDate = new Date(0);
          jsDate.setHours(0, 0, 0, 0);
          jsDate.setFullYear(this.year, 0, 1);
          this.date = Number.isNaN(jsDate.getTime()) ? undefined : jsDate;
        } catch {
          this.date = undefined;
        }
        this.valid = true;
        return;
      }
    }

    if (!GDTFUtil.isGDTF(gdtf)) {
      // Not a standard GDTF — continue with best-effort parsing
    }

    const unsigned = gdtfWithoutY.replace(/^-/, "");
    const filled = unsigned.replace(/x/g, isNegativeYear ? "9" : "0");
    const parts = filled.split("-");
    if (!parts || parts.length < 1) {
      // Failed to split — return invalid
      return;
    }

    const rawYear = Number(parts[0]);
    const year = isNegativeYear ? -rawYear : rawYear;
    if (isNaN(year) || year === 0) {
      // Invalid year — return invalid
      return;
    }
    this.year = year;

    if (parts[1]) {
      const month = Number(parts[1].replace(/^0/, ""));
      if (isNaN(month) || month < 1 || 12 < month) {
        // Invalid month — return invalid
        return;
      }
      this.month = month;
    }

    if (parts[2]) {
      const day = Number(parts[2].replace(/^0/, ""));
      if (isNaN(day) || day < 1 || 31 < day) {
        // Invalid day — return invalid
        return;
      }
      this.day = day;
    }

    try {
      const m = this.month ?? 1;
      const d = this.day ?? 1;
      const jsDate = new Date(0);
      jsDate.setHours(0, 0, 0, 0);
      jsDate.setFullYear(this.year, m - 1, d);
      this.date = Number.isNaN(jsDate.getTime()) ? undefined : jsDate;
    } catch {
      this.date = undefined;
    }

    this.valid = true;
  }

  toString(delimiter = DEFAULT_DELIMITER) {
    if (!this.year) {
      return "";
    }
    return [this.year, this.month, this.day].filter((v) => v).join(delimiter);
  }

  toDateNumber() {
    const m = this.month ? this.month.toString().padStart(2, "0") : "01";
    const d = this.day ? this.day.toString().padStart(2, "0") : "01";
    return Number(`${this.year}${m}${d}`);
  }

  getGDTF() {
    return this.gdtf;
  }

  getYear() {
    return this.year;
  }

  getMonth() {
    return this.month;
  }

  getDay() {
    return this.day;
  }

  getDate() {
    return this.date;
  }

  isValid() {
    return this.valid;
  }
}
